import React, { useCallback, useEffect, useState } from 'react';
import { Clock3 } from 'lucide-react';
import { GhostButton, PrimaryButton } from './LmBits';
import { lmError } from './lmApi';

/**
 * Deleted-view retention policy (#775). The period is per organization and has
 * no default: until an Admin chooses one, deleted records are kept and can be
 * restored indefinitely. A purge is permanent, so changing the policy is an
 * explicit, confirmed action gated by `list_management:delete`.
 */
export default function LmRetention({ api, canEdit, onChanged }) {
    const [settings, setSettings] = useState(null);
    const [editing, setEditing] = useState(false);
    const [mode, setMode] = useState('keep');
    const [days, setDays] = useState('90');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        api.settings().then(setSettings).catch((err) => setError(lmError(err, 'Retention settings could not be loaded.')));
    }, [api]);
    useEffect(load, [load]);

    const current = settings?.deleted_retention_days ?? null;
    const min = settings?.min_days ?? 7;
    const max = settings?.max_days ?? 3650;

    const startEdit = () => {
        setMode(current ? 'purge' : 'keep');
        setDays(String(current || 90));
        setError(null);
        setEditing(true);
    };

    const parsed = Number(days);
    const daysValid = Number.isInteger(parsed) && parsed >= min && parsed <= max;

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            const next = await api.saveSettings({ deleted_retention_days: mode === 'keep' ? null : parsed });
            setSettings(next);
            setEditing(false);
            onChanged?.(next);
        } catch (err) {
            setError(lmError(err, 'The retention policy could not be saved.'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div data-testid="lm-retention" role="note"
             className="mb-3.5 px-3.5 py-2.5 border border-border rounded-[10px] bg-card text-[12.5px] text-ink-2">
            <div className="flex items-center gap-2.5 flex-wrap">
                <Clock3 className="w-3.5 h-3.5 text-muted-foreground shrink-0" aria-hidden="true" />
                <span className="flex-1 min-w-[220px]" data-testid="lm-retention-policy">
                    {settings === null && !error && 'Loading retention policy…'}
                    {settings !== null && (current
                        ? <>Deleted records are <strong className="text-foreground">permanently purged {current} days</strong> after deletion. CRM records are never affected.</>
                        : <>Deleted records are <strong className="text-foreground">kept until restored</strong> — nothing is purged automatically.</>)}
                </span>
                {canEdit && settings !== null && !editing && (
                    <GhostButton testId="lm-retention-edit" onClick={startEdit}>Change policy</GhostButton>
                )}
            </div>
            {editing && (
                <div className="mt-3 pt-3 border-t border-border flex flex-col gap-2" data-testid="lm-retention-form">
                    <label className="flex items-center gap-2 text-foreground">
                        <input type="radio" name="lm-retention" checked={mode === 'keep'} onChange={() => setMode('keep')}
                               data-testid="lm-retention-keep" />
                        Keep deleted records until they are restored
                    </label>
                    <label className="flex items-center gap-2 text-foreground flex-wrap">
                        <input type="radio" name="lm-retention" checked={mode === 'purge'} onChange={() => setMode('purge')}
                               data-testid="lm-retention-purge" />
                        Permanently purge deleted records after
                        <input type="number" min={min} max={max} value={days} disabled={mode !== 'purge'}
                               onChange={(e) => setDays(e.target.value)} aria-label="Days before purge"
                               data-testid="lm-retention-days"
                               className="w-20 h-[30px] px-2 border border-input rounded-[7px] bg-card text-foreground tabular-nums disabled:bg-muted disabled:text-muted-foreground" />
                        days
                    </label>
                    {mode === 'purge' && (
                        <p className="text-[12px] text-amber-800 dark:text-warn" data-testid="lm-retention-warning">
                            Purged records cannot be restored. Committed CRM records, commit history and the audit trail are kept.
                            {!daysValid && ` Choose between ${min} and ${max} days.`}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        <PrimaryButton testId="lm-retention-save" onClick={save}
                                       disabled={saving || (mode === 'purge' && !daysValid)}>
                            {saving ? 'Saving…' : 'Save policy'}
                        </PrimaryButton>
                        <GhostButton testId="lm-retention-cancel" onClick={() => setEditing(false)}>Cancel</GhostButton>
                    </div>
                </div>
            )}
            {error && <p className="mt-2 text-[12px] text-red-800 dark:text-bad" role="alert">{error}</p>}
        </div>
    );
}
